import { ChangeEvent, FC, useState } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import HistoryIcon from '@material-ui/icons/History';
import ConfirmDialog from 'components/ConfirmDialog';
import State from 'components/State/Custom';
import {
  AddressAutocomplete,
  isAddressMismatch,
  LocationMap,
  ParsedPlace,
} from 'components/Map';
import {
  formatLatitudeDMS,
  formatLongitudeDMS,
} from 'components/LocationDetail/utils';
import { CAN_CHANGE_JOB } from 'providers/permissions';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  AddressHistoryModal,
  VIEW_ADDRESS_HISTORY_TOOLTIP,
} from './address-history-modal';

export type AddressEditorField =
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'zip';

const MANUAL_EDIT_HELPER_TEXT =
  'Manual location input may vary from your selected location';

const ADDRESS_MISMATCH_WARNING =
  'The address found does not exactly match what you entered. Verify the location is correct before continuing.';

export type AddressEditorValues = {
  address1: string;
  address2: string;
  city: string;
  zip: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
};

export type AddressEditorProps = {
  values: AddressEditorValues;
  onFieldChange: (field: AddressEditorField, value: string) => void;
  onPlaceSelected: (parsed: ParsedPlace) => void;
  onCoordinatesChange: (lat: number, lng: number) => void;
  readOnly?: boolean;
  disabledFields?: Partial<Record<AddressEditorField, boolean>>;
  errors?: Record<string, string | undefined>;
  jobId?: string;
};

export const AddressEditor: FC<AddressEditorProps> = ({
  values,
  onFieldChange,
  onPlaceSelected,
  onCoordinatesChange,
  readOnly = false,
  disabledFields,
  errors,
  jobId,
}) => {
  const classes = useStyles();
  const { hasPermission } = PermissionsProvider.useContainer();

  const [searchAddress, setSearchAddress] = useState('');
  const [committedSearchAddress, setCommittedSearchAddress] = useState('');
  const [pendingPlace, setPendingPlace] = useState<ParsedPlace | null>(null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [hasSearchSelection, setHasSearchSelection] = useState(false);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [manuallyEditedFields, setManuallyEditedFields] = useState<
    Partial<Record<AddressEditorField, boolean>>
  >({});
  const [lastSelectedValues, setLastSelectedValues] = useState<
    Partial<Record<AddressEditorField, string>>
  >({});

  const canViewHistory = !!jobId && hasPermission(CAN_CHANGE_JOB);

  const latitudeDms = formatLatitudeDMS(values.latitude);
  const longitudeDms = formatLongitudeDMS(values.longitude);
  const mapPosition =
    values.latitude !== null && values.longitude !== null
      ? { lat: values.latitude, lng: values.longitude }
      : null;

  const fieldError = (field: string): string | undefined =>
    errors ? errors[field] : undefined;

  const isDisabled = (field: AddressEditorField): boolean =>
    readOnly || !!disabledFields?.[field];

  const markManuallyEdited = (field: AddressEditorField, newValue: string) => {
    if (!hasSearchSelection) return;
    if (newValue === lastSelectedValues[field]) {
      setManuallyEditedFields(prev => {
        if (!prev[field]) return prev;
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
      return;
    }
    setManuallyEditedFields(prev =>
      prev[field] ? prev : { ...prev, [field]: true },
    );
  };

  const getHelperText = (field: AddressEditorField): string | undefined => {
    const error = fieldError(field);
    if (error) return error;
    return manuallyEditedFields[field] ? MANUAL_EDIT_HELPER_TEXT : undefined;
  };

  const handleTextChange =
    (field: AddressEditorField) =>
    (
      event: ChangeEvent<{
        name?: string | undefined;
        value: unknown;
      }>,
    ) => {
      const next = event.target.value;
      if (typeof next !== 'string') return;
      if (next === values[field]) return;
      onFieldChange(field, next);
      setUserHasEdited(true);
      markManuallyEdited(field, next);
    };

  const handleStateChange = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const next = event.target.value;
    if (typeof next !== 'string') return;
    if (next === values.state) return;
    onFieldChange('state', next);
    setUserHasEdited(true);
    markManuallyEdited('state', next);
  };

  const snapshotSelectedValues = (parsed: ParsedPlace) => {
    setLastSelectedValues({
      address1: parsed.address1,
      address2: parsed.address2,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
    });
  };

  const applyPlace = (parsed: ParsedPlace) => {
    onPlaceSelected(parsed);
    setSearchAddress(parsed.formatted);
    setCommittedSearchAddress(parsed.formatted);
    setHasSearchSelection(true);
    setUserHasEdited(true);
    setManuallyEditedFields({});
    snapshotSelectedValues(parsed);
    if (isAddressMismatch(parsed)) setMismatchOpen(true);
  };

  const handleBeforeSelect = (parsed: ParsedPlace): boolean => {
    const hasCurrentValues = !!(
      values.address1 ||
      values.address2 ||
      values.city ||
      values.state ||
      values.zip
    );
    if (!hasCurrentValues) return true;
    if (!userHasEdited) return true;
    setPendingPlace(parsed);
    setConfirmOverwriteOpen(true);
    return false;
  };

  const applyOverwrite = () => {
    if (pendingPlace) applyPlace(pendingPlace);
    setConfirmOverwriteOpen(false);
    setPendingPlace(null);
  };

  const cancelOverwrite = () => {
    setSearchAddress(committedSearchAddress);
    setConfirmOverwriteOpen(false);
    setPendingPlace(null);
  };

  return (
    <>
      {!readOnly && (
        <Box className={classes.inputAddress}>
          <AddressAutocomplete
            value={searchAddress}
            onChange={setSearchAddress}
            onPlaceSelected={applyPlace}
            onBeforeSelect={handleBeforeSelect}
          />
        </Box>
      )}
      <Box display="flex" className={classes.addressRow}>
        <Box className={classes.addressFields}>
          <TextField
            className={classes.inputAddress}
            fullWidth
            value={values.address1}
            label="Address"
            disabled={isDisabled('address1')}
            onChange={handleTextChange('address1')}
            error={!!fieldError('address1')}
            helperText={getHelperText('address1')}
            inputProps={{ 'data-testid': 'address1-input' }}
            InputProps={
              canViewHistory
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={VIEW_ADDRESS_HISTORY_TOOLTIP}>
                          <IconButton
                            size="small"
                            aria-label={VIEW_ADDRESS_HISTORY_TOOLTIP}
                            onClick={() => setHistoryOpen(true)}
                            data-testid="address-history-button"
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
          <Box display="flex" className={classes.responsiveRow}>
            <TextField
              className={classes.inputMarginRight}
              fullWidth
              label="Address2"
              disabled={isDisabled('address2')}
              value={values.address2}
              onChange={handleTextChange('address2')}
              helperText={getHelperText('address2')}
              inputProps={{ 'data-testid': 'address2-input' }}
            />
            <TextField
              className={classes.inputAddress}
              fullWidth
              label="Zip"
              value={values.zip}
              disabled={isDisabled('zip')}
              onChange={handleTextChange('zip')}
              error={!!fieldError('zip')}
              helperText={getHelperText('zip')}
              inputProps={{ 'data-testid': 'zip-input' }}
            />
          </Box>
          <Box display="flex" className={classes.responsiveRow}>
            <TextField
              className={classes.inputMarginRight}
              fullWidth
              label="City"
              disabled={isDisabled('city')}
              value={values.city}
              onChange={handleTextChange('city')}
              error={!!fieldError('city')}
              helperText={getHelperText('city')}
              inputProps={{ 'data-testid': 'city-input' }}
            />
            <State
              onChange={handleStateChange}
              disabled={isDisabled('state')}
              value={values.state}
              error={fieldError('state')}
              helperText={getHelperText('state')}
            />
          </Box>
          <Box display="flex" className={classes.responsiveRow}>
            <TextField
              className={classes.inputMarginRight}
              fullWidth
              label="Latitude"
              value={latitudeDms}
              disabled
              inputProps={{ 'data-testid': 'latitude-input' }}
            />
            <TextField
              className={classes.inputAddress}
              fullWidth
              label="Longitude"
              value={longitudeDms}
              disabled
              inputProps={{ 'data-testid': 'longitude-input' }}
            />
          </Box>
        </Box>
        <Box className={classes.mapColumn}>
          <LocationMap
            position={mapPosition}
            draggable={!readOnly}
            onPositionChange={
              readOnly
                ? undefined
                : ({ lat, lng }) => onCoordinatesChange(lat, lng)
            }
          />
        </Box>
      </Box>
      <ConfirmDialog
        open={confirmOverwriteOpen}
        showWarning
        title="If you choose a different address, all your changes to the address will be lost. Do you want to continue?"
        confirmLabel="Continue"
        handleClose={cancelOverwrite}
        onConfirm={applyOverwrite}
      />
      <ConfirmDialog
        open={mismatchOpen}
        showWarning
        title={ADDRESS_MISMATCH_WARNING}
        cancelLabel="Close"
        handleClose={() => setMismatchOpen(false)}
      />
      {jobId && (
        <AddressHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          jobId={jobId}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  addressRow: {
    gap: 16,
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  addressFields: {
    flex: 1,
    minWidth: 0,
  },
  mapColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'stretch',
    marginTop: 15,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
      height: 300,
    },
  },
  responsiveRow: {
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  inputAddress: {
    margin: '15px 0px 0px',
  },
  inputMarginRight: {
    margin: '15px 15px 15px 0px',
  },
}));

export default AddressEditor;
